<script setup>
import { computed, nextTick, onMounted, ref, useSlots, watch } from 'vue'

import { usePassthroughValue } from '@/Utils/usePassthroughValue'

const slots = useSlots()

const hasLabelSlot = computed(() => {
  const render = slots.label
  return typeof render === 'function' && render().length > 0
})

const showLabelRow = computed(
  () => hasLabelSlot.value || (props.label != null && props.label !== '')
)

const props = defineProps({
  name: {
    type: String,
    required: true
  },
  type: {
    type: String,
    default: 'checkbox',
    validator: (value) => ['checkbox', 'radio', 'switch'].includes(value)
  },
  label: {
    type: String,
    default: null
  },
  labelPosition: {
    type: String,
    default: 'right'
  },
  size: {
    type: String,
    default: 'sm',
    validator: (value) => ['sm', 'md', 'lg'].includes(value)
  },
  modelValue: {
    // Boolean for a single toggle, Array when several checkboxes share one
    // v-model (native checkbox group semantics).
    type: [Boolean, Array],
    default: false,
  },
  inputValue: {
    type: [String, Number, Boolean],
    required: true
  },
  disabled: {
    type: Boolean,
    default: false
  },
  indeterminate: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits({
  // falsy payloads (false, '', 0) are valid; returning the value itself made Vue warn
  'update:modelValue': () => true,
  change: (value) => value !== undefined,
})
const checked = usePassthroughValue(props, emit)

async function onInputChange() {
  await nextTick()
  emit('change', checked.value)
}

const inputRef = ref(null)

function syncIndeterminate() {
  const el = inputRef.value
  if (!el) return
  el.indeterminate = props.indeterminate
}

watch(
  () => props.indeterminate,
  () => {
    syncIndeterminate()
  },
  { flush: 'post' }
)

onMounted(() => {
  syncIndeterminate()
})
</script>

<template>
  <label
    class="form-checkbox-label flex !m-0 items-center gap-2 select-none"
    :class="[
      type,
      labelPosition === 'left' ? 'flex-row-reverse' : 'flex-row',
      disabled ? 'cursor-not-allowed opacity-90' : 'cursor-pointer'
    ]"
  >
    <span
      class="relative inline-flex shrink-0 items-center justify-center"
      :class="'form-checkbox-control-' + size"
    >
      <input
        ref="inputRef"
        v-model="checked"
        type="checkbox"
        class="form-checkbox-input peer absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
        :disabled="disabled"
        :name="name"
        :value="inputValue"
        @change="onInputChange"
      />
      <span
        class="form-checkbox-indicator pointer-events-none relative flex shrink-0 items-center justify-center rounded !border !border-light-5 text-surface-darker peer-focus-visible:ring-2 peer-focus-visible:ring-main peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-surface-1 peer-checked:!border-transparent peer-checked:bg-light-1 peer-indeterminate:!border-transparent peer-indeterminate:bg-light-1 peer-disabled:!border-surface-6 peer-disabled:bg-surface-1 peer-checked:peer-disabled:bg-surface-6 peer-indeterminate:peer-disabled:bg-surface-6"
        :class="'form-checkbox-indicator-' + size"
        aria-hidden="true"
      >
        <svg
          class="form-checkbox-icon form-checkbox-icon-check absolute inset-0 m-auto h-full w-full max-h-full max-w-full"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2.5 6L5 8.5L9.5 3.5"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <svg
          class="form-checkbox-icon form-checkbox-icon-minus absolute inset-0 m-auto h-full w-full max-h-full max-w-full"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2.5 6H9.5"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
          />
        </svg>
      </span>
    </span>
    <span
      v-if="showLabelRow"
      class="min-w-0 flex-1 text-sm leading-tight"
      :class="disabled ? 'text-surface-6' : 'text-surface-text-2'"
    >
      <template v-if="label != null && label !== ''">{{ label }}</template>
      <slot name="label" />
    </span>
  </label>
</template>

<style>
/* Indicator is not a peer of input; peer-* on SVGs won't work — icon visibility via sibling combinator below. */
.form-checkbox-indicator .form-checkbox-icon-check,
.form-checkbox-indicator .form-checkbox-icon-minus {
  display: none;
}

.form-checkbox-input:checked + .form-checkbox-indicator .form-checkbox-icon-check {
  display: block;
}

.form-checkbox-input:indeterminate + .form-checkbox-indicator .form-checkbox-icon-minus {
  display: block;
}

.form-checkbox-input:indeterminate + .form-checkbox-indicator .form-checkbox-icon-check {
  display: none;
}

.form-checkbox-control-sm {
  @apply h-3 w-3;
}

.form-checkbox-control-md {
  @apply h-3.5 w-3.5;
}

.form-checkbox-control-lg {
  @apply h-4 w-4;
}

.form-checkbox-indicator-sm {
  @apply h-3 w-3;
}

.form-checkbox-indicator-md {
  @apply h-3.5 w-3.5;
}

.form-checkbox-indicator-lg {
  @apply h-4 w-4;
}
</style>
